import { ClosureInventoryStateEnum } from 'pages/Closures/types';

export enum Event {
  REFRESH_RECEPTIONS = 'REFRESH_RECEPTIONS',
  REFRESH_ESTIMATED_RECEPTIONS = 'REFRESH_ESTIMATED_RECEPTIONS',
  REFRESH_CHARACTERIZATIONS = 'REFRESH_CHARACTERIZATIONS',
  REFRESH_PRODUCTIONS = 'REFRESH_PRODUCTIONS',
  REFRESH_EXPEDITIONS = 'REFRESH_EXPEDITIONS',
  REFRESH_EXPEDITION_BUYER_CONFIRMATION = 'REFRESH_EXPEDITION_BUYER_CONFIRMATION',
  REFRESH_CLOSURES = 'REFRESH_CLOSURES',
  REFRESH_CLIENTS = 'REFRESH_CLIENTS',
  REFRESH_BUYERS = 'REFRESH_BUYERS',
  REFRESH_STREAMS = 'REFRESH_STREAMS',
  REFRESH_MATERIAL = 'REFRESH_MATERIAL',
  REFRESH_CLIENT_CONTRACTS = 'REFRESH_CLIENT_CONTRACTS',
  REFRESH_INVENTORIED_INBOUND_STOCKS = 'REFRESH_INVENTORIED_INBOUND_STOCKS',
  REFRESH_REFRESH_EXPEDITIONS_PORTIONS = 'REFRESH_REFRESH_EXPEDITIONS_PORTIONS',
  REFRESH_SORTING_CENTER_CONTRACTS = 'REFRESH_SORTING_CENTER_CONTRACTS',
  REFRESH_SORTING_CENTER = 'REFRESH_SORTING_CENTER',
  REFRESH_LATEST_CLOSURE = 'REFRESH_LATEST_CLOSURE',
  REFRESH_ADMIN_USER = 'REFRESH_ADMIN_USER',
}

export enum UserRole {
  ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN',
  ROLE_ADMIN = 'ROLE_ADMIN',
  ROLE_SUPPORT = 'ROLE_SUPPORT',
  ROLE_ASSISTANT = 'ROLE_ASSISTANT',
  ROLE_MANAGER = 'ROLE_MANAGER',
  ROLE_ACCEPTED_TOS = 'ROLE_ACCEPTED_TOS',
  ROLE_USER = 'ROLE_USER',
  ROLE_CONSULTATION = 'ROLE_CONSULTATION',
}

export enum ConditioningType {
  Bulk = 'bulk',
  Ball = 'ball',
  Package = 'package',
}

export enum ClosureStatusEnum {
  TO_CLOSE = 'to_close',
  CLOSED = 'closed',
  REOPEN = 'reopen',
}

export enum SortingCenterContractStatus {
  ONGOING = 'ongoing',
  CLOSED = 'closed',
  END_OF_PROCESSING = 'endOfProcessing',
  UPCOMING = 'upcoming',
}

export type MaterialStandard = {
  id: number;
  code: string;
  fullName: string;
  isSubMaterial: boolean;
};

export type Contact = {
  '@id': string;
  id: number;
  email: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
};

export type ContactPost = {
  email: string;
  phoneNumber: string;
  firstName?: string;
  lastName?: string;
};

export type Material = {
  '@id': string;
  shortName: string;
  fullName?: string | null;
  id: number;
  conditioningType: ConditioningType;
  averageWeight: number | null;
  standard: MaterialStandard;
  isReject: boolean;
  hasExpeditions?: boolean;
  provisionalOutboundStock?: {
    conditioningQuantity?: number | null;
  };
  subMaterials?: SubMaterial[];
};

export type SubMaterial = {
  '@id': string;
  id: number;
  name: string;
  hasCharacterizationSubMaterial?: boolean;
};

export type InboundStock = {
  id: number;
  updateDateTime: Date;
  weight: number;
  client: Client;
  stream: Stream;
  sortingCenter: SortingCenter;
};

export type OutboundStock = {
  id: number;
  month: number;
  year: number;
  weight: number;
  client: Client;
  material: Material;
  sortingCenter: SortingCenter;
};

export type InventoriedInboundStock = {
  '@id'?: string;
  id?: string;
  month: string;
  year: string;
  weight?: number;
  stream: Stream;
  inventaireCheckbox?: boolean;
  separationDate?: string | null;
  separationHour?: string | null;
  sortingCenter: string;
  isActiveStream?: boolean;
};

export type InventoriedInboundStockPost = {
  id?: string;
  month: number;
  year: number;
  weight: number;
  stream: string;
  separationDate?: string | null;
  sortingCenter: string;
};

export type InventoriedOutboundStockBatch = {
  sortingCenter: string;
  inventoriedOutboundStocks: InventoriedOutboundStock[];
};

export type ExpeditionRepartitionAccepteeRepreneurBatch = {
  isMiseEnCoherence: boolean | null;
  sortingCenter: string;
  expeditionRepartitionAccepteeRepreneurBatch: ExpeditionRepartitionAccepteeRepreneurPost[];
  expeditionDate: string;
};

export type InventoriedOutboundStock = {
  '@id'?: string;
  id?: number | string;
  month: number | string;
  year: number | string;
  weight: number;
  averageWeight: number;
  calculatedAverageBallWeight: number;
  conditioningQuantity: number;
  material: string;
  sortingCenter: string;
};

export type Wastage = {
  id: number;
  validFrom: Date;
  wastageValue: number;
  wastageWeight: number | null;
  wastageFinalValue: number | null;
  wastageFinalWeight: number | null;
  sortingCenter: SortingCenter;
};

export type SortingCenterUserRole = {
  '@id': string;
  id?: number;
  user?: User;
  sortingCenter: SortingCenter;
  role: UserRole;
};

export type User = {
  '@id'?: string;
  roles: [UserRole];
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  sortingCenterUserRoles: SortingCenterUserRole[];
  id: string;
  createdAt?: string;
};

export type DistributionKey = {
  id: number;
  client: Client;
  material: Material;
  distributionKey: number;
  month: number;
  year: number;
};

export type ProvisionalOutboundStock = {
  sortingCenter: string;
  client: string;
  material: string;
  weight: number;
  conditioningQuantity?: number;
};

export type ConditionedStock = {
  sortingCenter: string;
  client: string;
  weightInKilos: number;
  conditioningQuantity: number;
  inventoryWeight: number;
  inventoryConditioningQuantity: number;
  inventoryMonth: string | number;
  inventoryYear: string | number;
  calculatedAverageBallWeightInKilos: number;
  inventoryAverageBallWeight: number;
  inventoryCalculatedAverageBallWeightInKilos: number;
  material: string;
  materialObject: Material;
  materialName: string;
  isMaterialBulk: boolean;
  inventoryIdApiPlatform?: string;
  inventoryId?: string | number;
};

export type OutboundStockByYearMonthByClientByMaterial = {
  [monthYear: string]: {
    [clientName: string]: {
      [materialName: string]: number;
    };
  };
};

export type ProvisionalOutboundStockByYearMonthByClientByMaterial = {
  [monthYear: string]: {
    [clientName: string]: {
      [materialName: string]: number;
    };
  };
};

export type ProvisionalOutboundStocksIndexedByStringId = {
  [key: string]: ProvisionalOutboundStock;
};

export type ProvisionalOutboundStocksAgregateByMaterialId = {
  [materialId: string]: ProvisionalOutboundStock;
};

export type InventoriedOutboundStocksAgregateByMaterialId = {
  [materialId: string]: InventoriedOutboundStock;
};

export type ProvisionalOutboundStocksIndexedByMaterialIdAndClientId = {
  [materialId: string]: {
    [clientId: string]: ProvisionalOutboundStock;
  };
};

export type MaterialNamesById = {
  [materialId: number]: string;
};

export type MaterialsById = {
  [materialId: number]: Material;
};

export type CalculatedAverageBallWeightByMaterialId = {
  [materialId: number]: number;
};

export type AverageBallWeight = {
  materialId: number;
  averageBallWeight: number;
};

export interface Buyer {
  '@id': string;
  name: string;
  id: number;
  addressLine?: string;
  postalCode: string;
  city: string;
  phoneNumber?: string;
  acceptedMaterials: Material[];
  contact: Contact;
}

export interface SortingCenter {
  '@id': string;
  name: string;
  id: number;
  code: string;
  addressLine?: string;
  postalCode: string;
  city: string;
  phoneNumber?: string;
  contact: Contact;
  initializationDate: string;
  isActive: boolean;
  contracts: SortingCenterContract[];
  inboundStocks: InboundStock[];
  outboundStocks: OutboundStock[];
  wastages: Wastage[];
  distributionKeys: DistributionKey[];
  parentSortingCenter?: SortingCenter | null;
  childrenSortingCenter?: SortingCenter[] | null;
  isActivatedMiseEnCoherenceObligatoire: boolean;
}

export interface Stream {
  '@id': string;
  id: number;
  name: string;
  composition: Material[];
  isActiveStream?: boolean | null;
}

export interface ChangeLog {
  '@id': string;
  id: number;
  version: string;
  releaseDate: string;
  content: string;
}

export interface InformationMessage {
  '@id': string;
  id: number;
  title: string;
  message: string;
  startDate: string;
  endDate: string;
  type: string;
}

export interface SortingCenterContractsEditFormPut {
  '@id'?: string | number | undefined;
  id: number | string;
  characterizationNumber: number;
  endDate: Date | string;
}

export interface SortingCenterContractsEditFormPutBatch {
  activeContracts: SortingCenterContractsEditFormPut[];
}

export enum InformationMessageType {
  PERMANENT = 'PERMANENT',
  EPHEMERE = 'EPHEMERE',
  BOTH = 'BOTH',
}

export interface QueryParams {
  [key: string]: string;
}

export interface SortingCenterContract {
  '@id': string;
  id: number;
  number: number;
  characterizationNumber: number;
  startDate: string;
  endDate: string;
  sortingCenter: SortingCenter;
  client: Client;
  stream: Stream;
  isBalanced: boolean;
  dateFermetureContrat: null | string;
}

export interface SortingCenterContractPost {
  client: string;
  stream: string;
  startDate: Date | null;
  endDate: Date | null;
  characterizationNumber: number | null;
  sortingCenter?: string;
}

export interface SortingCenterContractPut {
  '@id': string;
  id: number;
  client: string;
  stream: string;
  startDate: Date;
  endDate: Date;
  characterizationNumber: number | null;
  sortingCenter: string;
}

export interface SortingCenterContractFormType {
  client: string;
  stream: string;
  characterizationNumber: string | null;
  startDate: Date | null;
  endDate: Date | null;
  estimatedReceptionWeight: string | null;
  sortingCenter: string;
}

export interface SortingCenterContractEditFormType {
  '@id': string;
  id: number;
  client: string;
  stream: string;
  streamChoice: { '@id': string; name: string };
  characterizationNumber: string | null;
  startDate: Date;
  endDate: Date;
  estimatedReceptionWeight: string | null;
  estimatedReception: { '@id': string; month: number; year: number };
  sortingCenter: string;
}

export interface SortingCenterContractsRequirement {
  '@id': string;
  id: number;
  client: { '@id': string; id: number; shortName: string };
  stream: { '@id': string; name: string };
}

export interface SortingCenterContractsRequirements {
  '@id': string;
  id: number;
  contractsExpiringInThreeMonths: SortingCenterContractsRequirement[];
  contractsExpiringThisMonth: SortingCenterContractsRequirement[];
  contractsWithoutReceptions: SortingCenterContractsRequirement[];
  contractsWithoutReceptionsForTwoMonths: SortingCenterContractsRequirement[];
  contractsEndingAndNotClosedThisMonth: SortingCenterContractsRequirement[];
}

export interface ClientContract {
  id: number;
  sortingCenter: string; // @id
  client: string; // @id
  material: Material;
  buyer: Buyer;
  isActive: boolean;
}
export interface ClientContractCreation {
  sortingCenter: string; // @id
  client: string; // @id
  material: string;
  buyer: string;
  isActive: boolean;
}
export interface ClientContractCreateInterface {
  sortingCenter: number;
  materials: string[];
  buyer: string;
  isActive: boolean;
}

export interface CharacterizationMaterial {
  '@id': string;
  id: number;
  weight: number;
  sortingCenter: string;
  material: Material;
  characterization: Characterization;
  characterizationSubMaterials?: CharacterizationSubMaterial[];
}

export interface CharacterizationSubMaterial {
  '@id': string;
  id: number;
  weight: number;
  sortingCenter: SortingCenter;
  characterizationMaterial: CharacterizationMaterial;
  subMaterial: SubMaterial;
}

export interface Characterization {
  '@id': string;
  id: number;
  sortingCenter: SortingCenter;
  client: Client;
  stream: Stream;
  samplingDate: string;
  analyseDate: string;
  comment: string;
  weight: number;
  secteur: string;
  characterizationMaterials: CharacterizationMaterial[];
  isTheoreticalComposition: boolean | false;
  hasMultipleCharacterizations?: boolean | false;
}

export interface Client {
  '@id': string;
  shortName: string;
  fullName: string;
  id: number;
  code?: string;
  addressLine?: string;
  postalCode: string;
  city: string;
  phoneNumber?: string;
  inhabitants: number;
  contact: Contact;
  contracts: SortingCenterContract[];
  sortingCenterContractsStatus?: SortingCenterContractStatus;
}

export interface ClientPost {
  shortName: string;
  fullName: string;
  postalCode: string;
  city: string;
  sortingCenter: string;
  addressLine?: string;
  phoneNumber?: string;
  inhabitants?: number;
  contact?: ContactPost;
  code?: string;
}

export interface ExpeditionComputeRepartitionPost {
  mode: string;
  tonnageExpedie?: number;
  repartitionEnTonnage?: string[] | { [key: string]: string };
  repartitionEnPourcentage?: string[];
  numberOfDecimals?: number;
}

export interface Pagination {
  page: number;
  itemsPerPage: number;
}

export enum DialogWidth {
  DEFAULT = 'xs',
  DETAILS = 'sm',
  REFUSE = 'sm',
  LARGE_FORM = 'sm',
  SMALL_FORM = 'xs',
  LOADER = 'xs',
  MD_FORM = 'md',
}

export enum ProvisionalOutBoundStockQueryGroupByEnum {
  CLIENT = 'client',
  CLIENT_AND_MATERIAL = 'client_and_material',
  MATERIAL = 'material',
}

export type OrderDirection = 'asc' | 'desc';
export interface OrderFilter {
  order: OrderDirection;
  orderBy?: string | null;
}
export interface Reception {
  '@id': string;
  id: number;
  client: Client;
  stream: Stream;
  netWeight: number;
  grossWeight: number;
  tare: number;
  refused: boolean;
  incrementRejectStock: boolean;
  receptionDatetime: string;
  sortingCenter: SortingCenter;
  proofOfWeightIdentifier?: string;
}

export interface ReceptionForm {
  client: string;
  stream: string;
  netWeight: number | null;
  grossWeight: string | number | null;
  tare: string | number | null;
  refused: boolean;
  incrementRejectStock: boolean;
  receptionDate: Date | null;
  receptionTime: Date | null;
  proofOfWeightIdentifier: string;
}

export interface ReceptionPost {
  client: string;
  stream: string;
  netWeight: number;
  grossWeight: number | null;
  tare: number | null;
  refused: boolean;
  incrementRejectStock: boolean;
  receptionDatetime: string;
  sortingCenter?: string;
  proofOfWeightIdentifier: string | null;
}

export interface EstimatedReception {
  '@id': string;
  id: number;
  client: Client;
  stream: Stream;
  weight: number;
  month: number;
  year: number;
  sortingCenter: SortingCenter;
}

export interface EstimatedReceptionForm {
  formName: 'estimatedReceptionForm';
  month: number;
  year: number;
  sortingCenter: string;
  estimatedReceptions: {
    id: string;
    client: string;
    stream: string;
    weight: number;
  }[];
}

export interface EstimatedReceptionEditForm {
  '@id'?: string;
  weight: number;
}

export interface EstimatedReceptionPut {
  sortingCenter: string;
  estimatedReceptions: {
    id: string;
    client: string;
    stream: string;
    month: number;
    weight: number;
    year: number;
    sortingCenter: string;
  }[];
}

export interface EstimatedReceptionSinglePut {
  id: string;
  client: string;
  stream: string;
  month: number;
  weight: number;
  year: number;
  sortingCenter: string;
}

export interface ClientContractsPost {
  sortingCenter: string;
  clientContracts: ClientContractCreation[];
}

export interface EstimatedReceptionPost {
  sortingCenter: string;
  client: string;
  stream: string;
  month: number;
  weight: number;
  year: number;
}

export interface ExpeditionClientPortionForm {
  client: string;
  weight: string | null;
  percentage?: string | null;
  checked: boolean;
  availableStockWeight: number | null;
}

export interface ExpeditionForm {
  formName: 'expeditionForm';
  material: string;
  buyer: string;
  weight: number | '';
  conditioningQuantity: number | null | '';
  expeditionDate: Date | null;
  expeditionTime: Date | null;
  truckIdentifier: string;
  // Hack to be able to access the global expeditionClientPortions array error
  expeditionClientPortions: string | ExpeditionClientPortionForm[];
}

export interface ExpeditionBuyerConfirmationForm {
  formName: string;
  confirmationDate: Date | null;
  weight: number | null;
  decote: number | null;
}

export interface ExpeditionRepartitionTonnageRepreneurFormData {
  isMiseEnCoherence: boolean | null;
  month?: string;
  year?: string;
  isOneClosure?: boolean;
  expeditionRepartitionAccepteeRepreneur: {
    id: string;
    expeditionClientPortion: string;
    tonnageRepris: number;
    pourcentageRepris: number;
  }[];
  isForceMiseEnCoherence?: boolean;
}

export interface SortingCenterContractStatusByClient {
  clientId: number;
  status: SortingCenterContractStatus;
}

export interface ExpeditionRepartitionAccepteeRepreneurPost {
  id?: string;
  sortingCenter: string;
  expeditionClientPortion: string;
  tonnageRepris: number;
  pourcentageRepris: number;
}

export interface ExpeditionBuyerConfirmationMetaData {
  formValues: null | {
    confirmationDate: Date | null;
    decote: number | null;
    weight: number | null;
  };
  isEditMode: boolean;
  isEditRepartitionAccepteeRepreneurMode: boolean;
}

export interface ExpeditionBuyerConfirmationPost {
  confirmationDate: Date | null;
  weight: number | null;
  decote: number | null;
  sortingCenter: string;
  expedition: string;
}

export interface ExpeditionPost {
  material: string;
  buyer: string;
  weight: number;
  conditioningQuantity: number | null;
  expeditionDate: string;
  sortingCenter: string;
  truckIdentifier: string | null;
  expeditionClientPortions: {
    client: string;
    weight: number;
    percentage: number;
  }[];
}

export interface CharacterizationForm {
  id: string;
  formName: 'characterizationForm';
  client: string;
  stream: string;
  weight: number;
  samplingDate: Date | null;
  analyseDate: Date | null;
  comment: string | null;
  secteur: string | null;
  characterizationMaterials:
    | string // Hack to be able to display message on array level
    | {
        weight: number;
        material: string;
        characterizationSubMaterials?: {
          weight: string;
          subMaterial: string;
          sortingCenter: string;
        }[];
      }[];
}

export interface ClientCreateForm {
  formName: 'ClientCreateForm';
  name: string;
  fullName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  code: string;
  phoneNumber: string;
  inhabitants: string;
  contact: {
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
  };
}

export interface CharacterizationPost {
  client: string;
  stream: string;
  weight: number;
  samplingDate: Date;
  analyseDate: Date | null;
  sortingCenter: string;
  comment: string | null;
  secteur: string | null;
  characterizationMaterials: CharacterizationMaterialPost[];
}

export interface CharacterizationMaterialPost {
  weight: number;
  material: string;
  characterizationSubMaterials?: CharacterizationSubMaterialPost[];
}

export interface CharacterizationSubMaterialPost {
  weight: number | null;
  subMaterial: string;
  sortingCenter: string;
}

export interface ChangePasswordPost {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
}

export interface Expedition {
  '@id': string;
  id: number;
  buyer: Buyer;
  material: Material;
  expeditionDate: string;
  weight: number;
  isMiseEnCoherenceAccepted?: boolean;
  isUpdatedRepartitionAccepteeRepreneur?: boolean;
  conditioningQuantity?: number;
  truckIdentifier?: string;
  expeditionBuyerConfirmation?: ExpeditionBuyerConfirmation;
  expeditionClientPortions: ExpeditionClientPortion[];
  expeditionConfirmedClientPortions: ExpeditionConfirmedClientPortion[];
}

export interface ExpeditionBuyerConfirmation {
  '@id': string;
  id: string;
  confirmationDate: Date;
  weight: number;
  expedition: Expedition;
  decote: number;
  tonnageRecu: number;
}

export interface ClosedMonthsBoundaries {
  firstClosureDate: Date;
  lastClosureDate: Date;
}

export interface ExpeditionClientPortion {
  '@id': string;
  id: number;
  client: Client;
  material: Material;
  expedition: Expedition;
  availableStockWeight: number | null;
  weight: number;
  isUpdatedRepartitionAccepteeRepreneur: boolean;
  expeditionRepartitionAccepteeRepreneur?: ExpeditionRepartitionAccepteeRepreneur | null;
  tonnageReprisInTons?: number;
  percentage?: number;
}

export interface ExpeditionRepartitionAccepteeRepreneur {
  '@id': string;
  sortingCenter: SortingCenter;
  expeditionClientPortion: ExpeditionClientPortion;
  tonnageRepris: number;
  pourcentageRepris: number;
}

export interface ExpeditionConfirmedClientPortion {
  '@id': string;
  id: number;
  client: Client;
  material: Material;
  expedition: Expedition;
  weight: number;
}

export interface Production {
  '@id': string;
  id: number;
  material: Material;
  weight: number;
  productionDate: string;
  conditioningQuantity?: number;
}

export interface ClosureData {
  monthlyReceptions: Record<string, Record<string, number>>;
  wastage: number;
  monthlyWastage: number;
  finalAnnualWastagePercent?: number | null;
  annualClosure: boolean;
}

export type Closure = {
  '@id': string;
  id: number;
  year: number;
  month: number;
  sortingCenter: string;
  status: ClosureStatusEnum;
  inventoriedOutboundStockState: ClosureInventoryStateEnum;
  data: ClosureData | null;
};

export type ClosureForm = {
  '@id': string;
  closure: Closure;
  closureId: string;
  hasConfirmed: boolean;
};

export type ValidateViolationParameters = {
  [key: string]: string | number;
};

export type ValidateViolation = {
  message: string;
  code: null;
  propertyPath: string;
  title?: string;
  parameters?: ValidateViolationParameters;
};

export type ConstraintViolationList = {
  detail: string;
  title: string;
  type: string;
  violations: ValidateViolation[];
};

export type SubmissionError = {
  status: number;
  response: {
    body: {
      violations?: ValidateViolation[];
    };
  };
};

export type ClosureValidateViolation = ValidateViolation;

export type ClosurePost = {
  sortingCenter: string;
};

interface ProductionMaterial {
  id: string;
  conditioningQuantity: number | null;
  weight: number;
}

export interface ProductionForm {
  productionDate: Date | null;
  productionTime: Date | null;
  productionMaterials: ProductionMaterial[];
}

export interface InventoryFormInterface {
  conditionedStocks: ConditionedStock[];
}

export interface MaterialFormInterface {
  sortingCenter?: string | null;
  shortName: string;
  fullName: string;
  conditioningType: ConditioningType;
  averageWeight: number | null;
  standard: MaterialStandard;
}

export interface MaterialEditFormInterface {
  sortingCenter: string;
  shortName: string;
  fullName: string;
  subMaterials?: SubMaterial[];
}

export interface SingleProductionPost {
  weight: number;
  productionDate: Date;
  material: string;
  conditioningQuantity?: number | null;
  sortingCenter?: string;
}

export interface ProductionPost {
  sortingCenter: string;
  productions: SingleProductionPost[];
}

export interface BaseFilter {
  startDate?: Date | null;
  endDate?: Date | null;
}

export interface ExpeditionFilters extends BaseFilter {
  material?: string | null;
  buyer?: string | null;
  isMiseEnCoherenceAccepted?: number | null;
  invalidOnly?: boolean;
}

export interface CharacterizationFilters extends BaseFilter {
  client?: string | null;
  stream?: string | null;
  hasMultipleCharacterizations?: boolean | null;
  samplingDate?: Date | null;
}

export interface StreamFilters extends BaseFilter {
  isActiveStream?: boolean | null;
  sortingCenterId?: string;
  year?: string;
}

export interface ReceptionFilters extends BaseFilter {
  client?: string | null;
  stream?: string | null;
}

export interface ProductionFilters extends BaseFilter {
  material?: string | null;
}

export interface StockConditionneRepreneurFilters {
  buyerId?: string;
  materialId?: string;
}

export interface StockConditionneRepreneurExportParams {
  dateOfStockConditionneRepreneur: Date;
  buyerId?: string;
  materialId?: string;
}

export interface Filter extends BaseFilter {
  client?: string | null;
  stream?: string | null;
  material?: string | null;
  buyer?: string | null;
}

export type ExpeditionFiltersEncoded = {
  startDate: number | null;
  endDate: number | null;
  material: string | null;
  buyer: string | null;
  invalidOnly: boolean;
  isMiseEnCoherenceAccepted?: number | null;
};

export type AdministrationUserForPost = {
  email: string;
  firstName: string;
  lastName: string;
  plainPassword: string;
};

export type AdministrationSortingCenterUserRoleForPost = {
  sortingCenter: string;
  role: string;
};

export interface AdministrationUserCreateForm {
  formName?: 'AdministrationUserCreateForm';
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  sortingCenters?: string[];
  sortingCenterUserRole?: string[];
}

export interface AdministrationAddSortingCenterUserRoleForm {
  formName?: 'AdministrationAddSortingCenterUserRoleForm';
  userId?: string;
  sortingCenters?: boolean[];
  sortingCentersInitial?: boolean[];
  sortingCenterUserRole?: string[];
}

export type UserExistsByEmail = {
  isBelongToAdministrator?: boolean | null;
  user?: User | null;
};

export type MaximumInboundStocks = {
  maximumInboundStock: number;
};

export type StockConditionneRepreneur = {
  buyerName: string;
  materialName: string;
  tonnageKilo: number;
  nombreBalle: number | '-';
};

export type StockConditionneRepreneurByBuyerThenMaterial = StockConditionneRepreneur[][];

export type ListableEntity = Reception | Characterization | Expedition | Production | Client | Buyer;
